import React, { useState, useEffect, useRef } from "react";
import AuthLayout from "../../layout/AuthLayout";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomInput } from "../../components/Common/CustomInputs";
import {
  LockOutlined,
  KeyOutlined,
  ArrowBackOutlined,
  LoginOutlined,
} from "@mui/icons-material";
import { CustomBtn } from "../../components/Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import {
  ErrorSnackbar,
  ConfirmationSnackbar,
} from "../../components/Common/CustomSnackbar";

const RESEND_COOLDOWN_SECONDS = 60;

const TwoFactorVerify = ({ userId, email, password, onSuccess, onBack }) => {
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const cooldownRef = useRef(null);

  const [errorState, setErrorState] = useState({
    error: false,
    errorMsg: "",
  });
  const [successState, setSuccessState] = useState({
    success: false,
    successMsg: "",
  });

  // Start cooldown on mount (code was already sent by the login endpoint)
  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCooldown();
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setErrorState({ error: false, errorMsg: "" });
      await axiosInstance.post("/user/2fa/send-code", { userId });
      startCooldown();
      setSuccessState({
        success: true,
        successMsg: "A new verification code has been sent to your email.",
      });
    } catch (error) {
      if (error.response?.status === 429) {
        // Start cooldown so the user can't spam the button
        startCooldown();
        setErrorState({
          error: true,
          errorMsg: "Too many requests. Please wait a minute before trying again.",
        });
        return;
      }
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Failed to resend code.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter code input to digits only, max 6
  const handleCodeChange = (val) => {
    const filtered = val.replace(/\D/g, "").slice(0, 6);
    setCode(filtered);
  };

  const handleVerify = async () => {
    setErrorState({ error: false, errorMsg: "" });

    if (!useRecovery && code.length !== 6) {
      setErrorState({
        error: true,
        errorMsg: "Please enter a 6-digit verification code.",
      });
      return;
    }

    if (useRecovery && !recoveryCode.trim()) {
      setErrorState({
        error: true,
        errorMsg: "Please enter a recovery code.",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = { userId };

      if (useRecovery) {
        payload.recoveryCode = recoveryCode.trim().toLowerCase();
      } else {
        payload.token = code;
      }

      const response = await axiosInstance.post("/user/2fa/validate", payload);

      if (response.data.accessToken) {
        onSuccess(response.data);
      }
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Verification failed. Try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setErrorState({ error: false, errorMsg: "" });

    if (!recoveryCode.trim()) {
      setErrorState({
        error: true,
        errorMsg: "Please enter a recovery code to reset 2FA.",
      });
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("/user/2fa/reset", {
        email: email?.trim().toLowerCase(),
        password,
        recoveryCode: recoveryCode.trim().toLowerCase(),
      });

      setSuccessState({
        success: true,
        successMsg:
          "2FA has been disabled. You can now log in and will be asked to set up 2FA again.",
      });

      setTimeout(() => {
        onBack();
      }, 3000);
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Failed to reset 2FA. Try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Mask the email for display
  const maskedEmail = email
    ? `${email.slice(0, 3)}***@${email.split("@")[1]}`
    : "your email";

  return (
    <AuthLayout>
      <Box
        sx={{ px: 4, width: "100%" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (showReset) {
              handleReset();
            } else {
              handleVerify();
            }
          }
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Gotham', sans-serif",
            fontSize: "40px",
            color: theme.palette.text.primary,
          }}
        >
          {showReset ? "Reset 2FA" : "Two-Factor Authentication"}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Quicksand', sans-serif",
            fontSize: "18px",
            color: theme.palette.text.primary,
            mt: 1,
          }}
        >
          {showReset
            ? "Enter one of your recovery codes to disable 2FA. You will need to set it up again after logging in."
            : useRecovery
            ? "Enter one of your recovery codes"
            : `We've sent a verification code to ${maskedEmail}`}
        </Typography>

        <Box sx={{ my: 4, width: "100%" }}>
          {showReset || useRecovery ? (
            <CustomInput
              placeholder="Recovery code (e.g. xxxxx-xxxxx)"
              value={recoveryCode}
              setValue={setRecoveryCode}
              Icon={KeyOutlined}
              width={"100%"}
              color={theme.palette.text.primary}
            />
          ) : (
            <CustomInput
              placeholder="000000"
              value={code}
              setValue={handleCodeChange}
              Icon={LockOutlined}
              width={"100%"}
              color={theme.palette.text.primary}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: mediumScreen ? "column" : "row",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {showReset ? (
            <CustomBtn
              text="Reset 2FA"
              Icon={KeyOutlined}
              width={mediumScreen ? "60%" : "40%"}
              onClick={handleReset}
            />
          ) : (
            <CustomBtn
              text="Verify"
              Icon={LoginOutlined}
              width={mediumScreen ? "60%" : "30%"}
              onClick={handleVerify}
            />
          )}
        </Box>

        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          {!showReset && !useRecovery && (
            <Typography
              onClick={resendCooldown === 0 ? handleResendCode : undefined}
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: "16px",
                color: theme.palette.text.primary,
                textDecorationLine:
                  resendCooldown === 0 ? "underline" : "none",
                cursor: resendCooldown === 0 ? "pointer" : "default",
                opacity: resendCooldown === 0 ? 1 : 0.5,
              }}
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : "Resend code"}
            </Typography>
          )}

          {!showReset && (
            <Typography
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode("");
                setRecoveryCode("");
              }}
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: "16px",
                color: theme.palette.text.primary,
                textDecorationLine: "underline",
                cursor: "pointer",
              }}
            >
              {useRecovery
                ? "Use email code instead"
                : "Use a recovery code"}
            </Typography>
          )}

          <Typography
            onClick={() => {
              setShowReset(!showReset);
              setRecoveryCode("");
              setCode("");
              setUseRecovery(false);
            }}
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontSize: "16px",
              color: theme.palette.text.primary,
              textDecorationLine: "underline",
              cursor: "pointer",
            }}
          >
            {showReset ? "Back to verification" : "Need help? Reset 2FA"}
          </Typography>

          <Typography
            onClick={onBack}
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontSize: "16px",
              color: theme.palette.text.primary,
              textDecorationLine: "underline",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ArrowBackOutlined sx={{ fontSize: "16px" }} />
            Back to login
          </Typography>
        </Box>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
      <ConfirmationSnackbar
        snackbarState={successState.success}
        handleSnackbarClose={() =>
          setSuccessState({ success: false, successMsg: "" })
        }
        text={successState.successMsg}
      />
    </AuthLayout>
  );
};

export default TwoFactorVerify;
